import API from '@scale/frontend-lib/dashboard-api';
import React, { useState, useCallback } from 'react';
import { makeStyles } from 'design-system/product/theme';
import { SelfServeSupportedAttachmentTypes } from '@scale/shared/defaults';
import StaticAssetViewerModal from './StaticAssetViewerModal';
import { CircularProgress } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line restrict-imports/restrict-imports
import { IconButton, Tooltip } from '@material-ui/core';
import { UploadedAsset } from 'react-scale/dashboard.scale.com/components/SelfServe/types';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { useMount } from 'react-use-v13';

const getAssetType = (uploadedAsset: UploadedAsset) => {
  if (!uploadedAsset?.mimeType) return 'null';

  if (uploadedAsset.mimeType.match('image/*')) {
    return SelfServeSupportedAttachmentTypes.Image;
  } else if (uploadedAsset.mimeType.match('audio/*')) {
    return SelfServeSupportedAttachmentTypes.Audio;
  } else if (uploadedAsset.mimeType.match('video/*')) {
    return SelfServeSupportedAttachmentTypes.Video;
  } else if (uploadedAsset.mimeType.match('text/*') && uploadedAsset.text === undefined) {
    return SelfServeSupportedAttachmentTypes.DownloadableText;
  } else if (uploadedAsset.mimeType === 'text/json') {
    return SelfServeSupportedAttachmentTypes.Json;
  } else if (uploadedAsset.mimeType.match('text/*')) {
    return SelfServeSupportedAttachmentTypes.Text;
  } else if (uploadedAsset.mimeType === 'application/pdf') {
    return SelfServeSupportedAttachmentTypes.Pdf;
  } else if (uploadedAsset.mimeType === 'unprocessed') {
    return 'unprocessed';
  } else {
    return undefined;
  }
};

const getAsset = async (uploadedAsset: UploadedAsset) => {
  if (!uploadedAsset) return undefined;

  const attachmentType = getAssetType(uploadedAsset);

  if (
    (attachmentType === 'text' || attachmentType === SelfServeSupportedAttachmentTypes.Json) &&
    uploadedAsset.text
  ) {
    return uploadedAsset.text;
  } else {
    return (await API.selfserve.fetchAssetS3URL({ ...uploadedAsset, assetId: uploadedAsset._id }))
      .signedURL;
  }
};

interface StaticAssetPreviewProps {
  index: number;
  readOnly: boolean;
  preventOpen?: boolean;
  handleDelete?: () => void;
  staticAsset?: any;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  initialCaption?: string;
  submitCaption?: (caption: string) => void;
}

/*
 * render a preview of an example static asset
 * when user clicks on it, open modal with full example shown
 */
export const StaticAssetPreview: React.FC<StaticAssetPreviewProps> = ({
  readOnly,
  preventOpen,
  handleDelete,
  staticAsset,
  dragHandleProps,
  initialCaption,
  submitCaption,
}) => {
  const classes = useStyles();
  const [exampleViewerOpen, setExampleViewerOpen] = useState(false);
  const [staticAssetAttachment, setStaticAssetAttachment] = useState<string | undefined>(undefined);
  const [staticAssetAttachmentType, setStaticAssetAttachmentType] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);

  useMount(() => {
    getAsset(staticAsset)
      .then(urlOrText => {
        setStaticAssetAttachment(urlOrText);
        setStaticAssetAttachmentType(getAssetType(staticAsset));
        setIsLoading(false);
        return;
      })
      .catch(() => {
        setIsLoading(false);
      });
  });

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      handleDelete && handleDelete();
    },
    [handleDelete],
  );

  const renderDataPreview = (attachment: string, attachmentType: string) => {
    if (!attachment || !attachmentType) {
      return <div className={classes.text}>Error loading example.</div>;
    }

    // assume unprocessed is image for now
    if (attachmentType === 'image' || attachmentType === 'unprocessed') {
      return <img className={classes.img} src={attachment} crossOrigin="anonymous" />;
    } else if (attachmentType === 'text') {
      return <div className={classes.text}>{attachment}</div>;
    } else if (attachmentType === 'pdf') {
      return <object className={classes.pdf} data={attachment}></object>;
    } else if (attachmentType === 'audio' || attachmentType === 'transcription_audio') {
      return <audio className={classes.audio} src={attachment} controls />;
    } else if (attachmentType === 'website') {
      return <div className={classes.website}>{attachment}</div>;
    } else if (attachmentType === 'video') {
      return <video className={classes.video} src={attachment}></video>;
    } else {
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.label}>
      {exampleViewerOpen && (
        <StaticAssetViewerModal
          modalOpen={exampleViewerOpen}
          setModalOpen={arg => setExampleViewerOpen(arg)}
          staticAssetAttachment={staticAssetAttachment}
          staticAssetAttachmentType={staticAssetAttachmentType}
          initialCaption={initialCaption}
          submitCaption={submitCaption}
        />
      )}
      {
        <div
          onClick={async e => {
            e.preventDefault();
            if (!preventOpen) setExampleViewerOpen(true);
          }}
          style={{ cursor: preventOpen ? 'default' : 'pointer', width: '100%', height: '100%' }}
        >
          <div className={classes.preview}>
            {renderDataPreview(staticAssetAttachment!, staticAssetAttachmentType!)}
          </div>

          {!!initialCaption?.length && <div className={classes.caption}>{initialCaption}</div>}

          <div className={classes.overlay} />
          {!readOnly && (
            <div className={classes.checkbox} onClick={handleCheckboxClick}>
              <Tooltip title="Delete example asset">
                <IconButton size="small">
                  <FontAwesomeIcon icon="trash" color="white" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {!!dragHandleProps && !readOnly && (
            <div className={classes.dragIcon} {...dragHandleProps}>
              <Tooltip title="Drag to reorder asset">
                <IconButton size="small">
                  <FontAwesomeIcon icon="grip-vertical" color="white" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      }
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  overlay: {
    background: 'rgba(0, 0, 0, 0.25)',
    position: 'absolute',
    ...theme.alignment?.inset,
    opacity: 0,
    pointerEvents: 'none',
    transition: `opacity ${theme.duration?.short}ms ease-out`,
    zIndex: 1,
  },
  caption: {
    position: 'absolute',
    bottom: 0,
    color: 'black',
    fontSize: 14,
    background: 'rgba(255, 255, 255, 0.75)',
    padding: '0.25rem',
    maxWidth: '100%',
    maxHeight: 32,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  checkbox: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(4.5),
    opacity: 0,
    transform: `translate3d(0, ${theme?.spacing(-0.5)}px, 0)`,
    pointerEvents: 'none',
    transition: `all ${theme.duration?.short}ms ease-out`,
    zIndex: 2,
  },
  dragIcon: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    opacity: 0,
    transform: `translate3d(0, ${theme?.spacing(-0.5)}px, 0)`,
    pointerEvents: 'none',
    transition: `all ${theme.duration?.short}ms ease-out`,
    zIndex: 2,
  },
  checkboxRoot: {
    padding: 0,
    '@global svg': {
      height: 28,
      width: 28,
    },
  },
  label: {
    '&:hover $button': {
      opacity: 1,
    },
    position: 'relative',
    width: '100%',
    height: '100%',
    '&:hover $checkbox': {
      opacity: 1,
      transform: `translate3d(0, 0, 0)`,
      pointerEvents: 'all',
    },
    '&:hover $dragIcon': {
      opacity: 1,
      transform: `translate3d(0, 0, 0)`,
      pointerEvents: 'all',
    },
    '&:hover $overlay': {
      opacity: 1,
    },
  },
  button: {
    opacity: '0',
    top: '-6px',
    right: '-6px',
    position: 'absolute',
    zIndex: 1,
    color: 'gray',
  },
  preview: {
    width: '100%',
    height: '100%',
  },
  img: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    maxWidth: '100%',
  },
  pdf: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  audio: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
  },
  video: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
    padding: '2px',
    boxSizing: 'border-box',
  },
  website: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  text: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  loading: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
